import { motion } from "framer-motion";
import { MoveRight } from "lucide-react";
import { Reveal, EASE } from "@/components/Reveal";

const CARDS = [
  {
    step: "01",
    title: "POSITION",
    question: "Where is price?",
    desc: "Understand the market context before looking for a trade.",
    testid: "framework-card-position",
  },
  {
    step: "02",
    title: "EVENT",
    question: "What happened?",
    desc: "Wait for a qualifying market event.",
    testid: "framework-card-event",
  },
  {
    step: "03",
    title: "ENTRY",
    question: "Is there a reason to execute?",
    desc: "Act only when your process provides a defined reason.",
    testid: "framework-card-entry",
  },
];

function Connector() {
  return (
    <div className="flex items-center justify-center py-2 lg:py-0">
      <motion.div
        animate={{ x: [0, 8, 0], opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        className="text-neon"
      >
        <MoveRight size={26} className="hidden lg:block" />
        <MoveRight size={26} className="rotate-90 lg:hidden" />
      </motion.div>
    </div>
  );
}

export default function Framework() {
  return (
    <section id="framework" data-testid="framework-section" className="py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <Reveal>
            <div className="mb-5 flex items-center justify-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-neon/90">
              <span className="h-px w-8 bg-neon/50" />
              02 — The Framework
              <span className="h-px w-8 bg-neon/50" />
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-display text-4xl font-extrabold uppercase tracking-tight text-white sm:text-6xl">
              Position. <span className="text-neon">Event.</span> Entry.
            </h2>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-6 lg:grid-cols-[1fr_auto_1fr_auto_1fr] lg:items-stretch">
          {CARDS.map((c, i) => (
            <div key={c.step} className="contents">
              <Reveal delay={0.12 * i} className="h-full">
                <motion.div
                  data-testid={c.testid}
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="group flex h-full flex-col rounded-lg border border-line bg-charcoal p-8 transition-colors duration-500 hover:border-neon/50"
                >
                  <div className="font-mono text-xs tracking-[0.3em] text-neon">
                    {c.step}
                  </div>
                  <h3 className="mt-5 font-display text-2xl font-extrabold uppercase tracking-tight text-white">
                    {c.title}
                  </h3>
                  <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-mist">
                    {c.question}
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-mist">{c.desc}</p>
                  <div className="mt-auto pt-8">
                    <div className="h-px w-full bg-line transition-colors duration-500 group-hover:bg-neon/40" />
                  </div>
                </motion.div>
              </Reveal>
              {i < CARDS.length - 1 && <Connector />}
            </div>
          ))}
        </div>

        <div className="mt-20 space-y-3 text-center">
          {[
            { text: "Know your position.", tone: "text-white" },
            { text: "Wait for the event.", tone: "text-white" },
            { text: "Execute the entry.", tone: "glow-text-neon text-neon" },
          ].map((l, i) => (
            <Reveal key={l.text} delay={0.12 * i}>
              <div
                className={`font-display text-2xl font-extrabold uppercase tracking-tight sm:text-4xl ${l.tone}`}
              >
                {l.text}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
